'use client';

import { usePermissions } from '@modules/auth/hooks/usePermissions/usePermissions';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { getCleanLeaderRedirectUrl, setLeaderRedirectUrl } from '@utils/leaderRedirect';
import { isMembershipDefaultReturnTo } from 'app/_utils/auth/is-membership-default-return-to';
import { useGetMembershipsMutation } from 'app/_utils/data/useGetMemberships';
import { useUpdatePostLogin } from 'app/_utils/data/useUpdatePostLogin';

import * as Sentry from '@sentry/nextjs';
import { Route } from 'next';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { SystemRole } from 'shared/src/authz/rolesV2';
import { MemberAccessCheckType } from 'shared/src/generated/openapi/v3/types';

export type UsePostLoginMembershipRedirectArgs = {
  enabled: boolean;
  /** OIDC appState.returnTo from `/callback`; when set and not a default entry path, wins over membership shortcuts */
  explicitReturnTo?: string;
  /**
   * When true, call `POST /v2/system/post-login` before loading memberships. Use on `/callback` after token
   * exchange. Omit or false on `/` so repeat visits do not re-hit Zeus on every navigation.
   */
  runSystemPostLogin?: boolean;
};

/**
 * Optionally notifies the backend via `POST /v2/system/post-login` when `runSystemPostLogin` is true,
 * loads memberships, then routes with the same rules as `/callback` (leader redirect, explicit returnTo,
 * single-org shortcut when the user has no system cloud roles, then `/organizations`).
 */
export function usePostLoginMembershipRedirect({
  enabled,
  explicitReturnTo,
  runSystemPostLogin = false,
}: UsePostLoginMembershipRedirectArgs): void {
  const router = useRouter();
  const commonAuth = useCommonAuthProvider();
  const {
    cloudControlPlaneCdnUi,
    cloudMspEnableUiFeatures,
    cloudSkipPersistedLeaderRedirect,
  } = useFlagsProxy();
  const membershipsRequest = useGetMembershipsMutation(cloudMspEnableUiFeatures);
  const {
    update: updatePostLogin,
    isLoading: isUpdatePostLoginLoading,
  } = useUpdatePostLogin();
  const [postLoginCompleted, setPostLoginCompleted] = useState(false);

  const userSystemPermissions = usePermissions([
    {
      accessType: MemberAccessCheckType.System,
      checks: [SystemRole.ADMIN_READER, SystemRole.SE, SystemRole.SUPPORT],
    },
  ]);

  useEffect(() => {
    if (!enabled) {
      setPostLoginCompleted(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !commonAuth.user || postLoginCompleted) {
      return;
    }

    if (!runSystemPostLogin) {
      setPostLoginCompleted(true);

      return;
    }

    if (isUpdatePostLoginLoading) {
      return;
    }

    const handleUpdatePostLogin = async (): Promise<void> => {
      try {
        await updatePostLogin();
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setPostLoginCompleted(true);
      }
    };

    void handleUpdatePostLogin();
  }, [enabled, commonAuth.user, runSystemPostLogin, isUpdatePostLoginLoading, postLoginCompleted, updatePostLogin]);

  /* eslint-disable complexity */
  useEffect(() => {
    if (!enabled || !commonAuth.user || !postLoginCompleted || membershipsRequest.isMutating) {
      return;
    }

    const hasTriggeredOrLoadedMemberships = !membershipsRequest.data &&
      !membershipsRequest.error && !membershipsRequest.isMutating;

    if (hasTriggeredOrLoadedMemberships) {
      void membershipsRequest.trigger();

      return;
    }

    const hasMembershipsError = !membershipsRequest.isMutating && membershipsRequest.error;

    if (hasMembershipsError) {
      Sentry.captureException(membershipsRequest.error);
      router.push('/organizations' as Route);

      return;
    }

    if (!cloudSkipPersistedLeaderRedirect) {
      const leaderRedirectURL = getCleanLeaderRedirectUrl();

      if (leaderRedirectURL && leaderRedirectURL !== '/') {
        if (!cloudControlPlaneCdnUi) {
          window.location.assign(leaderRedirectURL);

          return;
        }

        const match = leaderRedirectURL?.match(/organizations\/(.*?)\//);
        const leaderOrgId = match ? match[1] : null;
        const hasLeaderOrganizationMembership = leaderOrgId && membershipsRequest.data?.some(
          (membership) => membership.organizationId === leaderOrgId,
        );

        if (hasLeaderOrganizationMembership) {
          setLeaderRedirectUrl(leaderRedirectURL);
          void commonAuth.loginWithRedirect({ organizationId: leaderOrgId });

          return;
        }

        router.push(`/organizations/${leaderOrgId}` as Route);

        return;
      }
    }

    const returnTo = explicitReturnTo;

    if (returnTo && !isMembershipDefaultReturnTo(returnTo)) {
      router.push(`${returnTo}` as Route);

      return;
    }

    const hasOnlyOneOrg = membershipsRequest.data?.length === 1;
    const hasCloudRole = Object.values(userSystemPermissions).some((val) => val);

    if (membershipsRequest.data && hasOnlyOneOrg && !hasCloudRole) {
      router.push(`/organizations/${membershipsRequest.data[0].tenant}` as Route);

      return;
    }

    router.push('/organizations' as Route);
  }, [
    enabled,
    commonAuth.user,
    commonAuth.loginWithRedirect,
    postLoginCompleted,
    membershipsRequest,
    cloudControlPlaneCdnUi,
    cloudSkipPersistedLeaderRedirect,
    router,
    userSystemPermissions,
    explicitReturnTo,
    runSystemPostLogin,
  ]);
  /* eslint-enable complexity */
}
